import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { HiExclamationCircle } from 'react-icons/hi';

type NotificationVariant = 'success' | 'error';

type NotificationProps = {
    variant: NotificationVariant;
};

const NotificationRoot = styled.div<NotificationProps>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    border: 1px solid
        ${({ variant }) =>
            variant === 'success' ? 'rgba(34, 197, 94, 0.18)' : 'rgba(239, 68, 68, 0.18)'};
    border-radius: 16px;
    background: ${({ variant }) =>
        variant === 'success' ? 'rgba(240, 253, 244, 0.88)' : 'rgba(254, 242, 242, 0.92)'};
`;

const NotificationHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SuccessText = styled.h3`
    margin: 0;
    color: #15803d;
    font-size: 14px;
    font-weight: 700;
`;

const ErrorText = styled.h3`
    margin: 0;
    color: #b91c1c;
    font-size: 14px;
    font-weight: 700;
`;

const ErrorDescription = styled.span`
    color: #991b1b;
    font-size: 13px;
    line-height: 1.5;
`;

const ErrorIcon = styled(HiExclamationCircle)`
    color: #dc2626;
    font-size: 18px;
    flex-shrink: 0;
`;

type ModalNotificationProps = {
    variant: NotificationVariant;
    title?: ReactNode;
    message: ReactNode;
};

export const ModalNotification = ({ variant, title, message }: ModalNotificationProps) => {
    if (variant === 'success') {
        return (
            <NotificationRoot variant="success">
                <SuccessText>{title ?? message}</SuccessText>
                {title && message ? <ErrorDescription>{message}</ErrorDescription> : null}
            </NotificationRoot>
        );
    }

    return (
        <NotificationRoot variant="error">
            <NotificationHeader>
                <ErrorIcon />
                <ErrorText>{title ?? 'Oops!'}</ErrorText>
            </NotificationHeader>
            <ErrorDescription>{message}</ErrorDescription>
        </NotificationRoot>
    );
};

export {
    NotificationRoot,
    NotificationHeader,
    SuccessText,
    ErrorText,
    ErrorDescription,
    ErrorIcon,
};

export default ModalNotification;
